import { Link } from 'react-router-dom';
import {
  Shield, Lock, Key, AlertTriangle, CheckCircle,
  Eye, Server, Code2, Mail, ArrowRight, Zap,
} from 'lucide-react';
import PageLayout from '../components/PageLayout';

function Section({ id, title, icon: Icon, accent = '#00E5FF', children }) {
  return (
    <section id={id} className="mb-14 scroll-mt-20">
      <div className="flex items-center gap-3 mb-5 pb-4 border-b border-white/[0.06]">
        <div
          className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ background: `${accent}15`, border: `1px solid ${accent}25` }}
        >
          <Icon size={16} style={{ color: accent }} />
        </div>
        <h2 className="text-xl font-bold text-white">{title}</h2>
      </div>
      <div className="space-y-4 text-slate-400 text-sm leading-relaxed">
        {children}
      </div>
    </section>
  );
}

function PracticeCard({ icon: Icon, title, items, accent }) {
  return (
    <div className="glass-card border border-white/[0.06] p-6">
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center mb-4"
        style={{ background: `${accent}15`, border: `1px solid ${accent}25` }}
      >
        <Icon size={17} style={{ color: accent }} />
      </div>
      <h3 className="text-sm font-bold text-white mb-3">{title}</h3>
      <ul className="space-y-2">
        {items.map((item, i) => (
          <li key={i} className="flex items-start gap-2 text-xs text-slate-500">
            <CheckCircle size={11} style={{ color: accent }} className="flex-shrink-0 mt-0.5" />
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}

const PRACTICES = [
  {
    icon: Lock,
    title: 'Encryption',
    accent: '#00E5FF',
    items: [
      'All data in transit encrypted with TLS 1.3',
      'Database fields encrypted at rest (AES-256)',
      'Passwords hashed with bcrypt (cost factor 12)',
      'JWT tokens signed with HS256, rotate every 7 days',
    ],
  },
  {
    icon: Server,
    title: 'Infrastructure',
    accent: '#7C3AED',
    items: [
      'Hosted on AWS (SOC 2 Type II)',
      'MongoDB Atlas with VPC peering, no public access',
      'Automated daily backups with 30-day retention',
      'DDoS protection via AWS Shield',
    ],
  },
  {
    icon: Key,
    title: 'Access Control',
    accent: '#f59e0b',
    items: [
      'Principle of least privilege for all internal systems',
      'MFA required for all team member accounts',
      'API keys scoped per user account and plan',
      'Rate limiting on all API endpoints',
    ],
  },
  {
    icon: Eye,
    title: 'Monitoring',
    accent: '#10b981',
    items: [
      '24/7 anomaly detection on API traffic',
      'Real-time alerting for authentication failures',
      'Audit logs retained for 90 days',
      'Automated vulnerability scanning on every deploy',
    ],
  },
  {
    icon: Code2,
    title: 'Secure Development',
    accent: '#f43f5e',
    items: [
      'Static analysis (ESLint security rules) on every PR',
      'Dependency vulnerability scanning via npm audit',
      'OWASP Top 10 review in quarterly security audits',
      'Security training for all engineers',
    ],
  },
  {
    icon: Shield,
    title: 'Compliance',
    accent: '#00E5FF',
    items: [
      'CORS policy restricting origins to known domains',
      'Helmet.js HTTP security headers on all responses',
      'GDPR-aligned data handling (see Privacy Policy)',
      'Annual third-party penetration test',
    ],
  },
];

const DISCLOSURE_STEPS = [
  {
    n: '01',
    title: 'Submit Report',
    desc: 'Email security@zenithcipher.io with details of the vulnerability. Include steps to reproduce, potential impact, and any proof-of-concept code.',
  },
  {
    n: '02',
    title: 'Acknowledgment',
    desc: "We will acknowledge your report within 48 hours and assign a severity level. We'll keep you informed of our investigation progress.",
  },
  {
    n: '03',
    title: 'Investigation & Fix',
    desc: 'Our security team investigates and develops a fix. For critical vulnerabilities, we aim to patch within 7 days. High severity within 30 days.',
  },
  {
    n: '04',
    title: 'Coordinated Disclosure',
    desc: 'After the fix is deployed, we coordinate public disclosure with you. We respect your preference for disclosure timing.',
  },
];

export default function SecurityPage() {
  return (
    <PageLayout
      badge="Security"
      badgeColor="cyan"
      title={<>Security at<br /><span className="text-[#00E5FF]">ZenithCipher</span></>}
      subtitle="How we protect your data, our platform, and what to do if you find a vulnerability."
    >
      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-14">

        {/* Security commitment banner */}
        <div className="mb-14 p-6 glass-card border border-[#00E5FF]/15 rounded-2xl flex flex-col sm:flex-row items-start gap-5">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#00E5FF] to-[#7C3AED] flex items-center justify-center flex-shrink-0 shadow-lg">
            <Shield size={22} className="text-white" />
          </div>
          <div>
            <h2 className="text-base font-bold text-white mb-2">Security is our product — and our culture</h2>
            <p className="text-slate-400 text-sm leading-relaxed max-w-2xl">
              We build a security intelligence platform, which means our own security posture must be
              beyond reproach. We apply the same scrutiny to our infrastructure that we ask our customers
              to apply to blockchain wallets. This page documents our security practices, responsible
              disclosure policy, and our commitments to you.
            </p>
          </div>
        </div>

        {/* Security practices grid */}
        <Section id="practices" title="Security Practices" icon={Lock}>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-2">
            {PRACTICES.map(p => <PracticeCard key={p.title} {...p} />)}
          </div>
        </Section>

        {/* Data security */}
        <Section id="data" title="How We Protect Your Data" icon={Eye} accent="#7C3AED">
          <p>
            Your scan reports and account information are stored in MongoDB Atlas with the following protections:
          </p>
          <div className="mt-4 space-y-3">
            {[
              { label: 'Database isolation',      desc: 'Your data is stored in a dedicated cluster with no public internet access. Access is restricted to our API servers via VPC peering.' },
              { label: 'Backup &amp; recovery',   desc: 'Automated daily snapshots with 30-day point-in-time recovery. RTO < 4 hours, RPO < 24 hours.' },
              { label: 'Field-level encryption',  desc: 'Sensitive fields (email addresses, API keys) are encrypted at rest using AES-256 before storage.' },
              { label: 'No raw credentials',      desc: 'Passwords are never stored in plaintext. We store only bcrypt hashes with a cost factor of 12.' },
              { label: 'Data minimisation',       desc: 'We only collect the data we need to operate the service. See our Privacy Policy for full details.' },
            ].map(({ label, desc }) => (
              <div key={label} className="flex items-start gap-3 p-4 glass-card border border-white/[0.06] rounded-xl">
                <CheckCircle size={14} className="text-[#7C3AED] flex-shrink-0 mt-0.5" />
                <div>
                  <div className="text-sm font-semibold text-white mb-0.5" dangerouslySetInnerHTML={{ __html: label }} />
                  <div className="text-xs text-slate-500 leading-relaxed">{desc}</div>
                </div>
              </div>
            ))}
          </div>
        </Section>

        {/* API security */}
        <Section id="api-security" title="API Security" icon={Key} accent="#f59e0b">
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { title: 'Bearer Token Auth',     desc: 'All authenticated endpoints require a signed JWT. Tokens expire and must be refreshed via re-authentication.' },
              { title: 'CORS Restrictions',     desc: 'API responses include strict CORS headers. Only approved origins can make credentialed requests.' },
              { title: 'Rate Limiting',         desc: 'All endpoints are rate-limited per IP. Exceeding limits returns 429 with Retry-After header.' },
              { title: 'Input Validation',      desc: 'Wallet addresses are validated against a strict regex. All user inputs are sanitised before processing.' },
              { title: 'Helmet.js Headers',     desc: 'All API responses include security headers: X-Content-Type-Options, X-Frame-Options, HSTS, CSP.' },
              { title: 'No Sensitive Logging',  desc: 'We never log API keys, passwords, or full wallet addresses in application logs.' },
            ].map(({ title, desc }) => (
              <div key={title} className="p-4 glass-card border border-white/[0.06] rounded-xl">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#f59e0b]" />
                  <div className="text-sm font-semibold text-white">{title}</div>
                </div>
                <div className="text-xs text-slate-500 leading-relaxed">{desc}</div>
              </div>
            ))}
          </div>
        </Section>

        {/* Responsible disclosure */}
        <Section id="disclosure" title="Responsible Disclosure Policy" icon={AlertTriangle} accent="#f43f5e">
          <p>
            We take security vulnerabilities seriously. If you discover a security issue in ZenithCipher,
            we ask that you follow responsible disclosure practices and give us the opportunity to fix it
            before public disclosure.
          </p>
          <p>
            <strong className="text-white">We commit to:</strong>
          </p>
          <div className="grid sm:grid-cols-2 gap-4 my-4">
            {[
              'Acknowledge your report within 48 hours',
              'Provide regular updates on fix progress',
              'Not pursue legal action for good-faith research',
              'Credit you in our security advisories (if desired)',
              'Target patches within 7 days for critical issues',
              'Coordinate disclosure timing with you',
            ].map(item => (
              <div key={item} className="flex items-start gap-2 text-sm text-slate-400">
                <CheckCircle size={13} className="text-green-400 flex-shrink-0 mt-0.5" />
                {item}
              </div>
            ))}
          </div>

          {/* Steps */}
          <div className="mt-8 grid sm:grid-cols-2 gap-5">
            {DISCLOSURE_STEPS.map(({ n, title, desc }) => (
              <div key={n} className="glass-card border border-white/[0.06] p-5 relative overflow-hidden">
                <div className="absolute top-4 right-4 text-5xl font-black text-white/[0.04] select-none leading-none">
                  {n}
                </div>
                <div className="text-[10px] font-bold text-[#f43f5e] tracking-widest mb-2">STEP {n}</div>
                <div className="text-sm font-bold text-white mb-2">{title}</div>
                <div className="text-xs text-slate-500 leading-relaxed">{desc}</div>
              </div>
            ))}
          </div>

          <div className="mt-8 p-5 glass-card border border-[#f43f5e]/15 bg-[#f43f5e]/5 rounded-xl">
            <h3 className="text-sm font-bold text-white mb-2 flex items-center gap-2">
              <AlertTriangle size={14} className="text-[#f43f5e]" />
              Out of Scope
            </h3>
            <p className="text-xs text-slate-400 leading-relaxed mb-3">
              The following are out of scope for our bug bounty and responsible disclosure program:
            </p>
            <div className="grid sm:grid-cols-2 gap-2">
              {[
                'Denial of service attacks',
                'Social engineering of ZenithCipher staff',
                'Physical security attacks',
                'Spam or email deliverability issues',
                'Vulnerabilities in third-party dependencies (report upstream)',
                'Issues requiring physical access to user devices',
              ].map(item => (
                <div key={item} className="flex items-start gap-2 text-xs text-slate-500">
                  <span className="text-[#f43f5e] mt-1 flex-shrink-0">✕</span>
                  {item}
                </div>
              ))}
            </div>
          </div>
        </Section>

        {/* Bug bounty */}
        <Section id="bounty" title="Bug Bounty Program" icon={Zap} accent="#10b981">
          <p>
            We offer rewards for responsibly disclosed vulnerabilities. Rewards are determined by
            severity, exploitability, and quality of the report.
          </p>
          <div className="mt-4 overflow-hidden glass-card border border-white/[0.06] rounded-xl">
            <table className="w-full">
              <thead>
                <tr className="text-[11px] text-slate-600 uppercase tracking-wider border-b border-white/[0.05]">
                  <th className="text-left px-5 py-3 font-medium">Severity</th>
                  <th className="text-left px-5 py-3 font-medium">Examples</th>
                  <th className="text-left px-5 py-3 font-medium">Reward</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/[0.04]">
                {[
                  { sev: 'Critical', color: '#f43f5e', ex: 'Auth bypass, RCE, data breach enabling full account takeover', reward: '$500 – $2,000' },
                  { sev: 'High',     color: '#f59e0b', ex: 'Privilege escalation, IDOR exposing other users\' data',         reward: '$100 – $500' },
                  { sev: 'Medium',   color: '#eab308', ex: 'Stored XSS, CSRF on sensitive actions, sensitive data exposure',  reward: '$50 – $100' },
                  { sev: 'Low',      color: '#10b981', ex: 'Reflected XSS, minor information disclosure',                    reward: 'Hall of Fame' },
                ].map(({ sev, color, ex, reward }) => (
                  <tr key={sev} className="hover:bg-white/[0.02] transition-colors">
                    <td className="px-5 py-3">
                      <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: `${color}15`, color }}>
                        {sev}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-xs text-slate-500">{ex}</td>
                    <td className="px-5 py-3 text-xs font-semibold text-white">{reward}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>

        {/* Contact */}
        <div className="glass-card border border-[#00E5FF]/15 p-8 rounded-2xl text-center">
          <Mail size={26} className="text-[#00E5FF] mx-auto mb-4" />
          <h3 className="text-xl font-bold text-white mb-2">Report a vulnerability</h3>
          <p className="text-slate-400 text-sm mb-6 max-w-md mx-auto">
            Found a security issue? Please email our security team directly. We take every report
            seriously and will respond within 48 hours.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href="mailto:security@zenithcipher.io"
              className="inline-flex items-center justify-center gap-2 bg-[#00E5FF] hover:bg-[#00cce5] text-black font-bold px-6 py-3 rounded-xl transition-all shadow-lg shadow-[#00E5FF]/20 text-sm group"
            >
              <Mail size={14} /> security@zenithcipher.io
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 border border-white/10 hover:border-white/20 bg-white/[0.04] text-slate-300 font-medium px-6 py-3 rounded-xl transition-all text-sm"
            >
              General Contact <ArrowRight size={13} />
            </Link>
          </div>
        </div>

      </div>
    </PageLayout>
  );
}
